import React, { useEffect, useState } from 'react';
import apiClient, { formatApiError } from '../utils/api';
import useGoogleDrivePicker from '../utils/useGoogleDrivePicker';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import * as PhosphorIcons from '@phosphor-icons/react';

const { Trash, Broadcast, Folder, MusicNotes, Warning } = PhosphorIcons;

const Announcements = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [driveStatus, setDriveStatus] = useState({ connected: false });
  const [driveConfig, setDriveConfig] = useState({ configured: false });

  // Picker → immediately opens the Save-form with the picked file preloaded.
  const [selectedFile, setSelectedFile] = useState(null);

  // Save form
  const [saveOpen, setSaveOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({ title: '', interval_minutes: 20 });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [a, g, d, c] = await Promise.all([
        apiClient.get('/announcements'),
        apiClient.get('/store-groups'),
        apiClient.get('/drive/status').catch(() => ({ data: { connected: false } })),
        apiClient.get('/drive/config').catch(() => ({ data: { configured: false } })),
      ]);
      setAnnouncements(a.data);
      setGroups(g.data);
      setDriveStatus(d.data || { connected: false });
      setDriveConfig(c.data || { configured: false });
    } catch (error) { toast.error(formatApiError(error)); }
    finally { setLoading(false); }
  };

  const handleConnectDrive = async () => {
    try {
      const res = await apiClient.get('/drive/connect');
      if (res.data.authorization_url) window.location.href = res.data.authorization_url;
    } catch (error) { toast.error(formatApiError(error)); }
  };

  // Single-file Picker — as soon as the user picks, open the Save form with
  // the file preselected. Simpler UX than the old two-step browse-then-save.
  const picker = useGoogleDrivePicker({
    audioOnly: true,
    multiSelect: false,
    onPicked: (files) => {
      const f = files?.[0];
      if (!f) return;
      setSelectedFile({ id: f.id, name: f.name, mimeType: f.mimeType });
      const cleanTitle = (f.name || 'Announcement').replace(/\.[^/.]+$/, '').replace(/[_-]/g, ' ');
      setFormData({ title: cleanTitle, interval_minutes: 20 });
      setSaveOpen(true);
    },
  });

  const openPicker = async () => {
    try { await picker.openPicker(); }
    catch (e) { toast.error(e.message || 'Could not open Google Picker'); }
  };

  const saveFromDrive = async (e) => {
    e.preventDefault();
    if (!selectedFile) return;
    setSaving(true);
    try {
      await apiClient.post('/announcements/add-from-drive', {
        drive_file_id: selectedFile.id,
        drive_file_name: selectedFile.name,
        drive_mime_type: selectedFile.mimeType,
        title: formData.title || selectedFile.name,
        interval_minutes: formData.interval_minutes,
      });
      toast.success('Announcement created — pushed to kiosks');
      setSaveOpen(false);
      setSelectedFile(null);
      setFormData({ title: '', interval_minutes: 20 });
      fetchData();
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (id, isActive) => {
    try {
      const a = announcements.find(x => x.id === id);
      await apiClient.put(`/announcements/${id}`, { is_active: !isActive, title: a?.title || '', interval_minutes: a?.interval_minutes || 20 });
      toast.success('Announcement updated');
      fetchData();
    } catch (error) { toast.error(formatApiError(error)); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this announcement?')) return;
    try {
      await apiClient.delete(`/announcements/${id}`);
      toast.success('Announcement deleted');
      fetchData();
    } catch (error) { toast.error(formatApiError(error)); }
  };

  const getGroupNames = (ids) => {
    if (!ids?.length) return 'All stores';
    return ids.map(id => groups.find(g => g.id === id)?.name).filter(Boolean).join(', ') || 'All stores';
  };

  if (loading) return <div className="text-[#6b665c] font-mono">Loading...</div>;

  return (
    <div className="space-y-6" data-testid="announcements-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold tracking-tight mb-2" data-testid="announcements-title">Announcements</h1>
          <p className="text-[#6b665c] font-mono text-sm tracking-wider uppercase">
            {driveStatus.connected ? 'Pick audio from Google Drive — pushed in real-time' : 'Connect Google Drive to add announcements'}
          </p>
        </div>
        {driveStatus.connected ? (
          <Button
            onClick={openPicker}
            disabled={driveStatus.connected && !driveConfig.configured}
            className="bg-blue-600 hover:bg-blue-700 text-white border-0 disabled:opacity-50"
            data-testid="pick-announcement-drive-button"
          >
            <Folder size={18} className="mr-2" />Pick from Drive
          </Button>
        ) : (
          <Button onClick={handleConnectDrive} className="bg-blue-600 hover:bg-blue-700 text-white border-0" data-testid="connect-drive-announcement">
            Connect Google Drive
          </Button>
        )}
      </div>

      {driveStatus.connected && !driveConfig.configured && (
        <div className="bg-amber-50 border border-amber-200 text-amber-900 rounded-md p-4 flex items-start gap-3" data-testid="ann-picker-not-configured">
          <Warning size={20} weight="bold" className="mt-0.5 flex-shrink-0 text-amber-600" />
          <div className="text-sm">
            <p className="font-semibold">Google Picker is not configured yet.</p>
            <p className="mt-1">
              Ask your MusicSync administrator to set <code className="bg-white/60 px-1 rounded">GOOGLE_API_KEY</code> in the backend environment.
              Existing announcements keep playing — only adding new ones is blocked until the key is set.
            </p>
          </div>
        </div>
      )}

      {!driveStatus.connected && (
        <div className="bg-white border border-[#c5d4c8] rounded-md p-8 text-center" data-testid="drive-ann-cta">
          <Folder size={48} className="text-[#a39e92] mx-auto mb-4" />
          <h3 className="text-lg font-bold text-[#2d2a26] mb-2">Connect Google Drive to add announcements</h3>
          <p className="text-sm text-[#6b665c] max-w-md mx-auto mb-4">
            All in-store ads are sourced from your Google Drive. Connect your account, pick the audio file, and it pushes to every kiosk in real time.
          </p>
          <Button onClick={handleConnectDrive} className="bg-blue-600 hover:bg-blue-700 text-white border-0">
            Connect Google Drive
          </Button>
        </div>
      )}

      {/* Save Dialog */}
      <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
        <DialogContent className="bg-white border-[#c5d4c8] text-[#2d2a26]">
          <DialogHeader><DialogTitle className="font-mono flex items-center gap-2"><Broadcast size={20} className="text-[#7c9082]" />New Announcement</DialogTitle></DialogHeader>
          <form onSubmit={saveFromDrive} className="space-y-4" data-testid="save-announcement-form">
            {selectedFile && (
              <div className="bg-[#f5f0e8] border border-[#c5d4c8] rounded-md p-3 flex items-center gap-2 text-sm">
                <MusicNotes size={16} className="text-[#7c9082]" />
                <span className="truncate">{selectedFile.name}</span>
              </div>
            )}
            <div>
              <Label className="font-mono text-xs uppercase tracking-wider text-[#5a6e5f]">Title</Label>
              <Input value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} className="mt-2 bg-[#f5f0e8] border-[#c5d4c8] text-[#2d2a26]" required data-testid="announcement-title-input" />
            </div>
            <div>
              <Label className="font-mono text-xs uppercase tracking-wider text-[#5a6e5f]">Interval (minutes)</Label>
              <Input type="number" value={formData.interval_minutes} onChange={(e) => setFormData({ ...formData, interval_minutes: parseInt(e.target.value, 10) || 20 })} className="mt-2 bg-[#f5f0e8] border-[#c5d4c8] text-[#2d2a26]" min="1" max="1440" required data-testid="announcement-interval-input" />
            </div>
            <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white border-0" disabled={saving} data-testid="submit-announcement-button">
              {saving ? 'Saving...' : 'Create & Push to Kiosks'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {announcements.length > 0 && (
        <div className="bg-white border border-[#c5d4c8] rounded-md overflow-hidden">
          <table className="w-full">
            <thead className="bg-[#f5f0e8] border-b border-[#c5d4c8]">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-mono uppercase tracking-wider text-[#6b665c]">Title</th>
                <th className="px-6 py-4 text-left text-xs font-mono uppercase tracking-wider text-[#6b665c]">Interval</th>
                <th className="px-6 py-4 text-left text-xs font-mono uppercase tracking-wider text-[#6b665c]">Groups</th>
                <th className="px-6 py-4 text-left text-xs font-mono uppercase tracking-wider text-[#6b665c]">Source</th>
                <th className="px-6 py-4 text-left text-xs font-mono uppercase tracking-wider text-[#6b665c]">Active</th>
                <th className="px-6 py-4 text-left text-xs font-mono uppercase tracking-wider text-[#6b665c]">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800">
              {announcements.map(ann => (
                <tr key={ann.id} className="hover:bg-[#f5f0e8] transition-all" data-testid={`announcement-row-${ann.id}`}>
                  <td className="px-6 py-4 font-medium">{ann.title}</td>
                  <td className="px-6 py-4 text-[#6b665c] font-mono text-sm">Every {ann.interval_minutes} min</td>
                  <td className="px-6 py-4 text-[#6b665c] text-xs">{getGroupNames(ann.group_ids)}</td>
                  <td className="px-6 py-4">
                    {ann.drive_file_id ? (
                      <span className="text-xs font-mono px-2 py-0.5 rounded-full bg-blue-900/30 text-[#7c9082] border border-[#c5d4c8]">Drive</span>
                    ) : (
                      <span className="text-xs font-mono px-2 py-0.5 rounded-full bg-[#e4ebe5] text-[#8a8477]">Legacy</span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <Switch checked={ann.is_active} onCheckedChange={() => handleToggleActive(ann.id, ann.is_active)} data-testid={`announcement-active-toggle-${ann.id}`} />
                  </td>
                  <td className="px-6 py-4">
                    <Button onClick={() => handleDelete(ann.id)} className="bg-[#fce8eb] hover:bg-[#f0c4cb] text-[#b5636e] border-0" data-testid={`delete-announcement-${ann.id}`}>
                      <Trash size={16} />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {announcements.length === 0 && driveStatus.connected && (
        <div className="bg-white border border-[#c5d4c8] rounded-md text-center py-12">
          <Broadcast size={48} className="text-[#a39e92] mx-auto mb-4" />
          <p className="text-[#8a8477] font-mono">No announcements yet. Pick one from Google Drive.</p>
        </div>
      )}
    </div>
  );
};

export default Announcements;
